import type { GuideTourPlacement } from '../types'
import type { SpotlightBounds } from '../overlay/utils'

export const GUIDE_TOUR_STEP_CARD_WIDTH = 360
export const GUIDE_TOUR_STEP_CARD_GAP = 16
export const GUIDE_TOUR_STEP_CARD_VIEWPORT_PADDING = 16

export type StepCardPositionInput = {
  bounds: SpotlightBounds
  placement: GuideTourPlacement
  cardWidth: number
  cardHeight: number
  viewportWidth: number
  viewportHeight: number
  gap?: number
  viewportPadding?: number
}

export type StepCardPosition = {
  top: number
  left: number
}

type ResolvedPlacement = Exclude<GuideTourPlacement, 'auto'>

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

const clampPosition = ({
  top,
  left,
  cardWidth,
  cardHeight,
  viewportWidth,
  viewportHeight,
  viewportPadding,
}: StepCardPosition & {
  cardWidth: number
  cardHeight: number
  viewportWidth: number
  viewportHeight: number
  viewportPadding: number
}): StepCardPosition => {
  const maxLeft = viewportWidth - cardWidth - viewportPadding
  const maxTop = viewportHeight - cardHeight - viewportPadding

  return {
    top: clamp(top, viewportPadding, maxTop),
    left: clamp(left, viewportPadding, maxLeft),
  }
}

const getPositionForPlacement = (
  placement: ResolvedPlacement,
  {
    bounds,
    cardWidth,
    cardHeight,
    gap = GUIDE_TOUR_STEP_CARD_GAP,
  }: Pick<StepCardPositionInput, 'bounds' | 'cardWidth' | 'cardHeight' | 'gap'>
): StepCardPosition => {
  const centeredLeft = bounds.left + bounds.width / 2 - cardWidth / 2
  const centeredTop = bounds.top + bounds.height / 2 - cardHeight / 2

  switch (placement) {
    case 'bottom':
      return {
        top: bounds.top + bounds.height + gap,
        left: centeredLeft,
      }
    case 'top':
      return {
        top: bounds.top - cardHeight - gap,
        left: centeredLeft,
      }
    case 'left':
      return {
        top: centeredTop,
        left: bounds.left - cardWidth - gap,
      }
    case 'right':
      return {
        top: centeredTop,
        left: bounds.left + bounds.width + gap,
      }
  }
}

const getVisibleCardArea = (
  position: StepCardPosition,
  cardWidth: number,
  cardHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  viewportPadding: number
): number => {
  const visibleLeft = Math.max(position.left, viewportPadding)
  const visibleTop = Math.max(position.top, viewportPadding)
  const visibleRight = Math.min(
    position.left + cardWidth,
    viewportWidth - viewportPadding
  )
  const visibleBottom = Math.min(
    position.top + cardHeight,
    viewportHeight - viewportPadding
  )

  return (
    Math.max(0, visibleRight - visibleLeft) *
    Math.max(0, visibleBottom - visibleTop)
  )
}

const fitsInsideViewport = (
  position: StepCardPosition,
  cardWidth: number,
  cardHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  viewportPadding: number
): boolean => {
  const cardArea = cardWidth * cardHeight
  const visibleArea = getVisibleCardArea(
    position,
    cardWidth,
    cardHeight,
    viewportWidth,
    viewportHeight,
    viewportPadding
  )

  return visibleArea >= cardArea
}

const resolvePlacement = ({
  bounds,
  placement,
  cardWidth,
  cardHeight,
  viewportWidth,
  viewportHeight,
  gap = GUIDE_TOUR_STEP_CARD_GAP,
  viewportPadding = GUIDE_TOUR_STEP_CARD_VIEWPORT_PADDING,
}: StepCardPositionInput): ResolvedPlacement => {
  if (placement !== 'auto') {
    return placement
  }

  const placementOptions: ResolvedPlacement[] = [
    'bottom',
    'top',
    'right',
    'left',
  ]

  const ranked = placementOptions
    .map(option => {
      const position = getPositionForPlacement(option, {
        bounds,
        cardWidth,
        cardHeight,
        gap,
      })

      return {
        option,
        position,
        visibleArea: getVisibleCardArea(
          position,
          cardWidth,
          cardHeight,
          viewportWidth,
          viewportHeight,
          viewportPadding
        ),
        fits: fitsInsideViewport(
          position,
          cardWidth,
          cardHeight,
          viewportWidth,
          viewportHeight,
          viewportPadding
        ),
      }
    })
    .sort((left, right) => {
      if (left.fits !== right.fits) {
        return Number(right.fits) - Number(left.fits)
      }

      return right.visibleArea - left.visibleArea
    })

  return ranked[0]?.option ?? 'bottom'
}

export const getScreenStepCardPosition = ({
  cardWidth,
  cardHeight,
  viewportWidth,
  viewportHeight,
  viewportPadding = GUIDE_TOUR_STEP_CARD_VIEWPORT_PADDING,
}: {
  cardWidth: number
  cardHeight: number
  viewportWidth: number
  viewportHeight: number
  viewportPadding?: number
}): StepCardPosition =>
  clampPosition({
    top: viewportHeight / 2 - cardHeight / 2,
    left: viewportWidth / 2 - cardWidth / 2,
    cardWidth,
    cardHeight,
    viewportWidth,
    viewportHeight,
    viewportPadding,
  })

export const getStepCardPosition = (
  input: StepCardPositionInput
): StepCardPosition => {
  const {
    bounds,
    cardWidth,
    cardHeight,
    viewportWidth,
    viewportHeight,
    gap = GUIDE_TOUR_STEP_CARD_GAP,
    viewportPadding = GUIDE_TOUR_STEP_CARD_VIEWPORT_PADDING,
  } = input

  const resolvedPlacement = resolvePlacement(input)
  const position = getPositionForPlacement(resolvedPlacement, {
    bounds,
    cardWidth,
    cardHeight,
    gap,
  })

  return clampPosition({
    ...position,
    cardWidth,
    cardHeight,
    viewportWidth,
    viewportHeight,
    viewportPadding,
  })
}
